/**
 * Miro's floating bar above the selection: its colour (a swatch or any colour), its words' font,
 * size (typed, `#board-size`, or a step at a time), bold, italic, underline, strike, colour
 * (`#board-text-colour`) and alignment, a picture's crop, a shape's outline, an arrow's line and
 * head; and with several items selected, lining them up, spreading them out, grouping; front and
 * back, duplicate. Every button is one undo (`[data-style=<key>][data-value=<value>]`,
 * `[data-board-action=<name>]`).
 *
 * The GUI packs taking part add groups of their own after the words' tools (`packs.sections()`;
 * UI/UX's: a frame's device, auto layout and "Component", an element's corners, an input's
 * hidden words, a checkbox's tick). In a pack's board mode the bar holds only that mode's groups
 * (UI/UX's prototype mode: where a click leads, or for a screen "Start here").
 *
 * It stays while words are being written: a press on it keeps the keyboard in the words (but in
 * its fields), so their look changes as they are typed. This file places the bar and picks its
 * groups of tools; each group is a file of its own beside it, reading and writing through `bar.ts`.
 */
import { For, Show, createSignal, onCleanup, type JSX } from "solid-js";
import { Dynamic } from "solid-js/web";
import { useApp } from "../../../../app/context";
import { bounds } from "../../../../state/board";
import { DESIGN } from "../../../../state/packs";
import { ManyTools, OrderTools, ShapeTools } from "./ArrangeTools";
import { ArrowTools } from "./ArrowTools";
import { createBar } from "./bar";
import { ColourTools } from "./ColourTools";
import { PictureTools } from "./PictureTools";
import { TextTools } from "./TextTools";
import { BAR_ABOVE_PX, BAR_SIDE_PX, BAR_TOP_MIN_PX } from "../constants";

/** The bar, placed above the selection's bounds on screen. */
export function StyleBar(): JSX.Element {
  const app = useApp();
  const { board, packs, kinds } = app;
  const bar = createBar(app);
  const { link, shape, many } = bar;
  /** Whether words are written on what is selected (not a frame, nor an element without words). */
  const worded = () => shape() !== "frame" && !kinds.element(shape()).wordless;
  /** The packs' groups for the selection, in the mode in use. */
  const sections = () => packs.sections().filter((s) => s.when(bar));
  /** Where the bar goes: above the selection (or the arrow's middle). */
  const at = () => {
    const places = board.content().places;
    const l = link();
    const rects = l ? [places.get(l.from), places.get(l.to)].filter((p) => !!p) : board.selected().flatMap((id) => (places.has(id) ? [places.get(id)!] : []));
    const b = bounds(rects);
    if (!b) return null;
    const c = board.camera();
    const overFrame = !l && board.selected().some((id) => board.content().frames.some((f) => f.id === id));
    return {
      x: (b.x + b.w / 2) * c.k + c.x,
      y: Math.max(BAR_TOP_MIN_PX, b.y * c.k + c.y - BAR_ABOVE_PX.item - (overFrame ? BAR_ABOVE_PX.frameTitle : 0)),
    };
  };
  /** The bar's width and the board's, so the bar stays inside the board. */
  const [widths, setWidths] = createSignal({ bar: 0, stage: 0 });
  let observer: ResizeObserver | undefined;
  onCleanup(() => observer?.disconnect());
  /** Watches the bar's size. */
  const measure = (el: HTMLDivElement) => {
    observer?.disconnect();
    const update = () => setWidths({ bar: el.offsetWidth, stage: el.parentElement?.clientWidth ?? 0 });
    observer = new ResizeObserver(update);
    observer.observe(el);
    queueMicrotask(update);
  };
  /** The bar's middle, kept so that the whole bar is on the board. */
  const left = (x: number) => {
    const { bar: width, stage } = widths();
    if (!width || !stage) return x;
    const half = width / 2 + BAR_SIDE_PX;
    return stage < width + BAR_SIDE_PX * 2 ? stage / 2 : Math.min(stage - half, Math.max(half, x));
  };
  return (
    <Show when={!board.busy() && at()}>
      {(p) => (
        <div
          ref={measure}
          class="board-stylebar"
          id="board-stylebar"
          style={{ left: `${left(p().x)}px`, top: `${p().y}px` }}
          onPointerDown={(e) => e.stopPropagation()}
          // The words being written keep the keyboard (a field of the bar takes it).
          onMouseDown={(e) => !(e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) && e.preventDefault()}
          onDblClick={(e) => e.stopPropagation()}
        >
          <Show when={packs.mode() !== DESIGN}>
            <For each={sections()}>{(s) => <Dynamic component={s.Tools} bar={bar} />}</For>
          </Show>
          <Show when={packs.mode() === DESIGN}>
            <ColourTools bar={bar} />
            <Show when={shape() === "image"}>
              <PictureTools bar={bar} />
            </Show>
            <Show when={link()}>
              <ArrowTools bar={bar} />
            </Show>
            <Show when={!link()}>
              <Show when={worded()}>
                <TextTools bar={bar} />
              </Show>
              <For each={sections()}>{(s) => <Dynamic component={s.Tools} bar={bar} />}</For>
              <Show when={shape() === "shape"}>
                <ShapeTools bar={bar} />
              </Show>
              <Show when={many()}>
                <ManyTools />
              </Show>
              <Show when={shape() !== "frame"}>
                <OrderTools />
              </Show>
            </Show>
          </Show>
        </div>
      )}
    </Show>
  );
}
